import { ocppStatusCopy } from '../../../utils/statusCopy.js';
import { availabilityCopy } from '../../../chargerUx/copy.js';

/**
 * Availability categories used only to decide the card's primary
 * action label and to keep the "available first" order under a tie
 * in the sort. The user-facing status text still comes from the
 * shared `ocppStatusCopy()` — this file does not invent new copy
 * or a separate operational-status policy (no heartbeat rules, no
 * timeout-based OFFLINE). The API's `connector_status` remains the
 * source of truth.
 */
export const AVAILABILITY = Object.freeze({
  AVAILABLE: 'AVAILABLE',
  IN_USE: 'IN_USE',
  UNAVAILABLE: 'UNAVAILABLE',
  MAINTENANCE: 'MAINTENANCE',
  UNKNOWN: 'UNKNOWN',
});

const IN_USE = new Set([
  'Preparing',
  'Charging',
  'SuspendedEV',
  'SuspendedEVSE',
  'Finishing',
  'Occupied',
  'Reserved',
  'Busy',
]);

const UNAVAILABLE = new Set(['Unavailable', 'Faulted', 'Disabled']);

export function classifyAvailability(status) {
  if (status === 'Available') return AVAILABILITY.AVAILABLE;
  if (IN_USE.has(status)) return AVAILABILITY.IN_USE;
  if (UNAVAILABLE.has(status)) return AVAILABILITY.UNAVAILABLE;
  return AVAILABILITY.UNKNOWN;
}

/**
 * Station-level classifier. Maintenance mode is an administrative
 * override on top of OCPP status — a station that is technically
 * `Available` at the OCPP layer must render as `MAINTENANCE` when
 * the admin flag is set, so the user cannot start a session on it.
 *
 * Accepts the API row shape (top-level `maintenance_mode`) as well
 * as normalised camelCase (`maintenanceMode`) so it works from
 * either side of the map/list plumbing.
 */
/**
 * Charger UX: when the API sends its own `availability` for a charger
 * (Available / In use / Temporarily unavailable / Offline / Out of service),
 * that is the answer — it already accounts for maintenance, connectivity and
 * faults, and it never carries a fault code. Mapped onto the categories the
 * cards and the sort already use. Null when the API sent none.
 */
const API_AVAILABILITY_CATEGORY = Object.freeze({
  AVAILABLE: AVAILABILITY.AVAILABLE,
  IN_USE: AVAILABILITY.IN_USE,
  TEMPORARILY_UNAVAILABLE: AVAILABILITY.UNAVAILABLE,
  OFFLINE: AVAILABILITY.UNAVAILABLE,
  OUT_OF_SERVICE: AVAILABILITY.UNAVAILABLE,
});

function apiAvailability(station) {
  const copy = availabilityCopy(station?.availability);
  return copy ? { ...copy, category: API_AVAILABILITY_CATEGORY[copy.code] } : null;
}

export function classifyStationAvailability(station) {
  if (!station || typeof station !== 'object') return AVAILABILITY.UNKNOWN;
  const api = apiAvailability(station);
  if (api) return api.category;
  if (station.maintenance_mode === true || station.maintenanceMode === true) {
    return AVAILABILITY.MAINTENANCE;
  }
  return classifyAvailability(station.connector_status ?? station.connectorStatus);
}

export function statusDisplay(status) {
  return ocppStatusCopy(status);
}

/**
 * Composite copy resolver that lets the maintenance override win
 * over the underlying OCPP status. Call this from card / map
 * surfaces where the visible label should reflect the admin state
 * rather than the raw connector status.
 */
export function stationStatusDisplay(station) {
  if (!station || typeof station !== 'object') return statusDisplay(undefined);
  const api = apiAvailability(station);
  if (api) return { label: api.label, tone: api.tone };
  if (station.maintenance_mode === true || station.maintenanceMode === true) {
    return { label: 'Under Maintenance', tone: 'warning' };
  }
  return statusDisplay(station.connector_status ?? station.connectorStatus);
}
